import React, { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { PaymentSplit } from 'react/types/payment';
import ErrorMessage from 'react/components/error_message';
import { Invoice } from 'react/types/invoice';
import Input from 'react/components/input';
import DateInput from '../../components/date_input';
import notification from 'helpers/notification';
import contactType from 'helpers/contact_type';
import api from 'helpers/api';
import dateFormatter from 'helpers/date_formatter';
import { useAngularService } from 'helpers/get_angular_service';
import { useExtendState } from 'helpers/use_extend_state';
import Button from '../../components/button';
import { Gym } from '../../types/gym';
import currencyFormatter from 'helpers/currency_formatter';

type SplitPaymentModalProps = {
  modalInstance: any;
  invoice: Invoice;
};

const SplitPaymentModal = ({ modalInstance, invoice }: SplitPaymentModalProps) => {
  const $rootScope = useAngularService('$rootScope');
  const gym: Gym = $rootScope.gym;
  const [error, setError] = useState('');
  const [dateError, setDateError] = useState('');
  const [paymentSplit, extendPaymentSplit] = useExtendState<PaymentSplit>({
    new_amount: 0,
    invoice_id: invoice.id,
    remainder_amount: 0,
    new_date: invoice.due_date,
  });
  const [debouncedNewAmount] = useDebounce(paymentSplit.new_amount, 300);

  const currency = gym.currency_symbol;

  const cancel = () => {
    modalInstance.dismiss('cancel');
  };

  const submit = () => {
    const url = `/gyms/${gym.id}/${contactType(invoice.contact_type).toLocaleLowerCase()}/${
      invoice.contact_id
    }/payment_splits`;
    api.post(url, { payment_split: paymentSplit }).then(
      () => {
        $rootScope.$emit('invoices:list:reload');
        $rootScope.$emit('contact:reload');
        cancel();
        notification.success('Payment split successful');
      },
      (error) => {
        setError(error.data.error);
        setDateError(error.data.date_error);
        notification.error('Unable to split payment: please review the error below');
      },
    );
  };

  const isReady = () => {
    return paymentSplit.new_amount >= 1;
  };

  useEffect(() => {
    extendPaymentSplit({ remainder_amount: Number(invoice.amount) - debouncedNewAmount });
    if (paymentSplit.new_amount > 0 && paymentSplit.new_amount < 1) {
      setError('Amount must be 1 or higher');
    } else {
      setError('');
    }
  }, [debouncedNewAmount, invoice.amount]);

  return (
    <div>
      <div className="modal-header">
        <a aria-hidden="true" className="close-modal" onClick={cancel} title="Close">
          <i className="icon-cross"></i>
        </a>
        <h4 className="modal-title">Split payment amount</h4>
      </div>
      <div className="modal-body modal-body-visible">
        <div className="margin-bottom-16">
          <strong>Contact:</strong> {invoice.contact_name}
          <br />
          <strong>Membership:</strong> {invoice.membership_label}
        </div>
        <div className="margin-bottom-16">
          <strong>Payment:</strong> {currencyFormatter(invoice.amount, gym.currency_symbol)}, due{' '}
          {dateFormatter(invoice.due_date)}
        </div>
        <div className="margin-bottom-16">
          To divide this into two separate amounts, specify the value of the new payment
          below:
        </div>
        <div className="form-group row">
          <div className="col-md-4">
            <label>New payment amount:</label>
          </div>

          <div className="col-md-6">
            <div className="relative">
              <Input
                prefix={currency}
                min={0}
                value={paymentSplit.new_amount}
                onChange={(new_amount) => extendPaymentSplit({ new_amount })}
              />
            </div>
            <ErrorMessage error={error} />
          </div>
        </div>

        <div className="form-group row">
          <div className="col-md-4">
            <label>Amount remaining:</label>
          </div>

          <div className="col-md-4">
            <span>
              {paymentSplit.new_amount
                ? currencyFormatter(
                    Number(invoice.amount) - paymentSplit.new_amount,
                    gym.currency_symbol,
                  )
                : currencyFormatter(invoice.amount, gym.currency_symbol)}
            </span>
          </div>
        </div>

        <div className="form-group row">
          <div className="col-md-4">
            <label>New payment date:</label>
          </div>

          <div className="col-md-6">
            <DateInput
              value={paymentSplit.new_date}
              onChange={(new_date) => extendPaymentSplit({ new_date })}
              hideClearDate
            />
            <ErrorMessage error={dateError} />
          </div>
        </div>

        {paymentSplit.new_amount >= 1 && (
          <div className="margin-bottom-16">
            A new payment for{' '}
            <strong>{currencyFormatter(paymentSplit.new_amount, gym.currency_symbol)}</strong> will
            be added on <strong>{dateFormatter(paymentSplit.new_date)}</strong>, and the current
            payment's amount will be reduced to{' '}
            <strong>{currencyFormatter(paymentSplit.remainder_amount, gym.currency_symbol)}</strong>
            .
          </div>
        )}

        {Number(invoice.credit_applied_amount) > 0 &&
          Number(invoice.credit_applied_amount) === paymentSplit.remainder_amount && (
            <div className="margin-bottom-16">
              Due to an existing credit balance, the current payment will be{' '}
              <strong>automatically marked as paid by credit</strong> by this change.
            </div>
          )}

        {Number(invoice.credit_applied_amount) > 0 &&
          Number(invoice.credit_applied_amount) > paymentSplit.remainder_amount && (
            <div className="margin-bottom-16">
              Due to an existing credit balance, the current payment will be{' '}
              <strong>automatically marked as paid by credit</strong> by this change,
              and the leftover credit will be transferred to the new payment.
            </div>
          )}
      </div>
      <div className="modal-footer">
        <Button onClick={cancel}>Cancel</Button>
        <Button variant="success" onClick={() => submit()} disabled={!isReady()}>
          Split payment
        </Button>
      </div>
    </div>
  );
};

angular.module('gym').value('SplitPaymentModal', SplitPaymentModal);
